import { MainContent } from '@/components/content'
import { MainLayout } from '@/components/layouts'
import { SettingSidebar } from '@/components/sidebar'
import { BenefitClaimModal } from '@/components/modal/BenefitClaimModal'
import { ROUTE_CLAIM_BENEFIT } from '@/constants/Routes'
import { BundlePartView, HeldBenefit, PartKinds, PartnerRedemptionView, useMyBenefits } from '@/services/myBenefits'
import { ClaimTargetLink } from '../components/ClaimTargetLink'
import { RedemptionSteps } from '../components/RedemptionSteps'
import { claimOptions, partnersOf, usablePolicies, visibleBenefits } from '../claimOptions'
import { groupBenefits, startsExpanded } from '../groupBenefits'
import { redemptionText } from '../redemptionText'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

/**
 * The member's benefits, and the way in to claiming one.
 *
 * The picker itself lives at [ROUTE_CLAIM_BENEFIT], not here — it is what a paywall, a push or a
 * grant email links to, and it needs a URL of its own. This page opens the same component inline
 * for the ordinary case of tapping Claim on a card, so a member who is already looking at their
 * list is not bounced through a navigation to reach a dialog.
 */
export const MyBenefits = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: benefits, isLoading, isError, refetch } = useMyBenefits()
  const [searchParams, setSearchParams] = useSearchParams()
  const [claiming, setClaiming] = useState<HeldBenefit | null>(null)

  const claimParam = searchParams.get('claim')

  // `?claim=<grantId>` predates the dedicated route and is still in the wild — in grant emails
  // already sent, and in any paywall configured before the route existed. Forward it rather than
  // maintaining two ways to open the same picker, which is how the two drift.
  useEffect(() => {
    if (!claimParam) return
    // Validated before it becomes part of a path. A grant id is a number; anything else is a
    // malformed or hand-edited link, and interpolating it verbatim would put attacker-chosen text
    // into a route. A bad value drops to the plain list, which is where a stale link belongs.
    const grantId = Number(claimParam)
    if (!Number.isSafeInteger(grantId) || grantId <= 0) {
      searchParams.delete('claim')
      setSearchParams(searchParams, { replace: true })
      return
    }
    navigate(`${ROUTE_CLAIM_BENEFIT}/${grantId}`, { replace: true })
  }, [claimParam, navigate, searchParams, setSearchParams])

  const closeClaim = () => setClaiming(null)

  // A bundle's parts are shown inside it, and a benefit with nowhere to spend it not at all.
  const held = visibleBenefits(benefits ?? [])

  return (
    // The same chrome as Subscriptions, which is the page this one sits beside in the settings
    // sidebar — and the sidebar is where the count badge lives, so a member who lands here from a
    // deep link can still see the rest of their account. Rendered bare, this page had no navigation
    // at all: you could arrive but not go anywhere.
    <MainLayout>
      <SettingSidebar />
      <MainContent>
        <div className="mx-auto max-w-3xl px-4 py-6">
          {/* On the app's blue gradient, not on white — every dashboard page paints its own surfaces
          and its heading sits directly on the background. Dark heading text here was legible only
          because nothing had checked it against the ground it actually lands on. */}
          <h1 className="text-2xl font-bold text-white">{t('benefits.title')}</h1>
          <p className="mt-1 text-sm text-white text-opacity-80">{t('benefits.subtitle')}</p>

          {isLoading && <p className="py-10 text-center text-sm text-white text-opacity-70">{t('benefits.loading')}</p>}

          {/* A failed fetch is not "you have no benefits". The empty state is a statement about what
          the member owns, and rendering it for a failed request tells someone holding an unclaimed
          credit that they hold nothing — with no retry and nothing to mark it as a guess. */}
          {!isLoading && isError && (
            <div className="mt-5 rounded-lg bg-white p-6 text-center">
              <p className="text-base font-medium text-gray-800">{t('benefits.loadFailedTitle')}</p>
              <p className="mt-1 text-sm text-gray-500">{t('benefits.loadFailedDesc')}</p>
              <button className="mt-3 text-sm text-gray-900 underline" onClick={() => refetch()}>
                {t('benefits.tryAgain')}
              </button>
            </div>
          )}

          {!isLoading && !isError && held.length === 0 && (
            <div className="mt-5 rounded-lg bg-white p-6 text-center">
              <p className="text-base font-medium text-gray-800">{t('benefits.emptyTitle')}</p>
              <p className="mt-1 text-sm text-gray-500">{t('benefits.emptyDesc')}</p>
            </div>
          )}

          {!isLoading && !isError && held.length > 0 && <BenefitSections benefits={held} onClaim={setClaiming} />}

          <BenefitClaimModal
            open={claiming !== null}
            onClose={closeClaim}
            benefit={claiming}
            // Tapping Claim on a card opens the picker, so there is no fixed target and no
            // selection to lift out — the list refreshes itself on success.
            fixedTarget={null}
            onClaimed={null}
            selectedTargetUrn={null}
            onSelectTarget={null}
          />
        </div>
      </MainContent>
    </MainLayout>
  )
}

/**
 * The list, split by what the member can do with each row.
 *
 * ## Three groups, and only one of them closed
 *
 * A flat list put a spent benefit from March above an unclaimed one expiring on Friday, and the
 * cards are not small — a claimed one carries its instructions and, increasingly, a QR code. So the
 * list is grouped (see `groupBenefits`) and the finished group is an accordion: still reachable,
 * because a member needs to see where a benefit went long after spending it, but closed so history
 * cannot bury the thing they came here for.
 *
 * An empty group renders nothing at all rather than an empty heading — "Claimed (0)" is a row that
 * costs space to say nothing.
 */
const BenefitSections = ({
  benefits,
  onClaim,
}: {
  benefits: HeldBenefit[]
  onClaim: (benefit: HeldBenefit) => void
}) => {
  const { t } = useTranslation()
  // Closed on arrival. A member opens this page to find something they can still use; what they
  // already spent is a thing they go looking for, not a thing that should be in the way.
  const [historyOpen, setHistoryOpen] = useState(false)
  const grouped = groupBenefits(benefits)

  const rows = (list: HeldBenefit[]) => (
    <div className="mt-2 space-y-3">
      {list.map((benefit) => (
        <BenefitCard key={benefit.grantId} benefit={benefit} onClaim={() => onClaim(benefit)} />
      ))}
    </div>
  )

  return (
    <div className="mt-5 space-y-6">
      {grouped.claimable.length > 0 && (
        <section>
          <Heading label={t('benefits.sectionClaimable')} count={grouped.claimable.length} />
          {rows(grouped.claimable)}
        </section>
      )}

      {grouped.claimed.length > 0 && (
        <section>
          <Heading label={t('benefits.sectionClaimed')} count={grouped.claimed.length} />
          {rows(grouped.claimed)}
        </section>
      )}

      {grouped.history.length > 0 && (
        <section>
          <button
            type="button"
            className="flex w-full items-center justify-between py-1 text-left"
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((open) => !open)}
          >
            <Heading label={t('benefits.sectionHistory')} count={grouped.history.length} />
            <span aria-hidden className="text-xs text-white text-opacity-70">
              {historyOpen ? '▲' : '▼'}
            </span>
          </button>
          {/* Unmounted rather than hidden: a closed accordion should cost nothing, and these cards
              each render a QR and an identity block. */}
          {historyOpen && rows(grouped.history)}
        </section>
      )}
    </div>
  )
}

/**
 * A group heading.
 *
 * The count is on every heading, not just the collapsed one: it answers "have I got anything left"
 * without reading the rows, and a heading that only sometimes carries a number reads as two
 * different components.
 */
const Heading = ({ label, count }: { label: string; count: number }) => (
  <h2 className="text-xs font-semibold uppercase tracking-wide text-white text-opacity-80">
    {label} ({count})
  </h2>
)

const STATE_LABEL: Record<HeldBenefit['state'], string> = {
  AVAILABLE: 'benefits.available',
  ASSIGNED: 'benefits.claimed',
  PENDING_STEP: 'benefits.awaitingTarget',
  REDEEMED: 'benefits.redeemed',
  EXPIRED: 'benefits.expired',
}

/**
 * The tone of each state, so the list reads at a glance.
 *
 * Only two states are good news and only one is bad; the rest are neutral facts. Color is never
 * the only signal — every card also states its status in words directly beneath the label.
 */
const STATE_TONE: Record<HeldBenefit['state'], string> = {
  // The mock-up's own badge palette: a tint of the state's color behind the darker shade of it,
  // never a solid fill. A black pill read as a button on a card full of them.
  AVAILABLE: 'bg-[#3A79E0]/10 text-[#204590]',
  ASSIGNED: 'bg-[#00C874]/10 text-[#007846]',
  PENDING_STEP: 'bg-[#EE9F45]/10 text-[#8A5A12]',
  REDEEMED: 'bg-[#8C8C8C]/10 text-[#7A7A7A]',
  EXPIRED: 'bg-[#8C8C8C]/10 text-[#7A7A7A]',
}

const BenefitCard = ({ benefit, onClaim }: { benefit: HeldBenefit; onClaim: () => void }) => {
  const { t } = useTranslation()
  const claimable = benefit.state === 'AVAILABLE'
  // Partners are never claimed in the app — the member follows the partner's link instead — so the
  // Claim button is only for a benefit with somewhere in the app to claim it: a kind of place it
  // names or, for a bundle, a part that names one. Nothing else gets one; see `claimOptions`.
  // A bundle of several benefits shows each part's partners under that part (see `BundlePart`): a
  // partner part is used alongside the others, not instead of them.
  const partners = claimable && !benefit.parts ? partnersOf(benefit) : []
  const claimableInApp = claimable && claimOptions(benefit).length > 0
  // No label fallback: the heading above is already the program's name, and
  // a label fallback printed it a second time for every unclaimed benefit. This line
  // is for what a target actually settled the benefit at, which only exists once it is claimed.
  const worth = benefit.worth ?? null

  return (
    <div className={`rounded-lg border bg-white p-4 ${claimable ? 'border-gray-200' : 'border-gray-200 opacity-90'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold text-gray-900">{benefit.label ?? t('benefits.aBenefit')}</p>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_TONE[benefit.state]}`}>
              {t(STATE_LABEL[benefit.state])}
            </span>
          </div>

          {/* What it is worth. Only a claimed benefit has a number — an unclaimed one is worth
              whatever the target settles it at, so the program's name is all there is to say. */}
          {worth && <p className="mt-0.5 text-sm text-gray-600">{worth}</p>}

          <p className="mt-1 text-xs text-gray-500">
            {/* Where it went, by name, and a way back to it — which matters most while a claim
                is still pending on someone. The server resolves the name, so this never shows a
                URN. */}
            {benefit.claim && <ClaimTargetLink claim={benefit.claim} />}
            {claimable && t('benefits.expiresOn', { date: moment(benefit.expires).format('D MMM YYYY') })}
          </p>
        </div>

        {claimableInApp && (
          <button
            className="shrink-0 rounded-full bg-[#3A79E0] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2D5FB8]"
            onClick={onClaim}
          >
            {t('benefits.claimCta')}
          </button>
        )}
      </div>

      {/* A bundle made of several benefits: what is in it and where each part stands. Read-only —
          the one Claim above opens the picker, whose tabs are its claimable parts. */}
      {benefit.parts && <BundleParts part={benefit.parts} />}

      {partners.length > 0 && <PartnerOffers partners={partners} />}

      {/* Waiting on the target. Named for the step the server reported rather than assumed to be
          acceptance, so a workflow that grows a second step says something true without a change
          here. */}
      {benefit.state === 'PENDING_STEP' && benefit.claim && (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {t('benefits.pendingStepDesc', { targetName: benefit.claim.targetName })}
        </p>
      )}

      {/* Claiming reserved it; using it is a separate act, and what that act IS now comes off the
          benefit rather than being assumed to be a front desk. Unconditional: the server decides
          whether there is anything to say — it answers with a null object while a benefit is
          unclaimed and while a coach has yet to accept — and `RedemptionSteps` draws nothing when
          there is not. That check used to live here as `state === 'ASSIGNED'`, in both clients. */}
      {/* Closed once the benefit is finished; see `startsExpanded`. */}
      <RedemptionSteps redemption={benefit.redemption} worth={worth} initiallyExpanded={startsExpanded(benefit)} />
    </div>
  )
}

/** What each part's state says, in words. A state this build has never heard of says nothing. */
const PART_STATE_LABEL: Record<string, string> = {
  AVAILABLE: 'benefits.partAvailable',
  CLAIMED: 'benefits.partClaimed',
  RESERVED: 'benefits.partReserved',
  USED: 'benefits.partUsed',
  LOCKED: 'benefits.partLocked',
  EXPIRED: 'benefits.partExpired',
}

/**
 * A bundle's parts, nested as it was authored: a group says how its parts combine, in the server's
 * words ("One of these"), and each benefit says where it stands.
 *
 * A claimed part shows where it went and how to use it, exactly as a claimed benefit of its own
 * would — it IS one, with its own grant. An open part usable at a partner shows that partner's link
 * under it, there and nowhere else: in "a partner credit, and one of a club credit or a lesson" the
 * partner credit is used alongside the choice, not instead of it.
 */
const BundleParts = ({ part }: { part: BundlePartView }) => (
  <div className="mt-3 rounded-md bg-gray-50 px-3 py-2.5">
    <BundlePart part={part} />
  </div>
)

const BundlePart = ({ part }: { part: BundlePartView }) => {
  const { t } = useTranslation()
  if (part.kind !== PartKinds.Benefit) {
    return (
      <div>
        <p className="text-xs font-medium text-gray-700">{part.label}</p>
        <ul className="mt-1 space-y-1.5 border-l border-gray-200 pl-3">
          {(part.children ?? []).map((child) => (
            <li key={child.id}>
              <BundlePart part={child} />
            </li>
          ))}
        </ul>
      </div>
    )
  }
  const own = part.benefit ?? null
  // What it is worth: what it was claimed at once claimed, otherwise what claiming it would give.
  const worth = own?.worth ?? (own ? (usablePolicies(own)[0]?.worth ?? null) : null)
  const stateLabel = PART_STATE_LABEL[part.state]
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm text-gray-900">{part.label}</p>
        {stateLabel && <span className="text-xs text-gray-500">{t(stateLabel)}</span>}
      </div>
      {worth && worth !== part.label && <p className="text-xs text-gray-600">{worth}</p>}
      {own?.claim && (
        <p className="mt-0.5 text-xs text-gray-500">
          <ClaimTargetLink claim={own.claim} />
        </p>
      )}
      {own && (
        <RedemptionSteps
          redemption={own.redemption}
          worth={own.worth ?? null}
          initiallyExpanded={startsExpanded(own)}
        />
      )}
      {part.claimable && !!own?.partners?.length && <PartnerOffers partners={own.partners} />}
    </div>
  )
}

/**
 * Where an unclaimed benefit can be used at a partner, and the way there.
 *
 * Nothing is claimed here: the partner claims the benefit when the member uses it on the partner's
 * side, so each row is a link off DUPR rather than a button that changes anything in the app. The
 * link is the server's, already rendered for this member.
 */
const PartnerOffers = ({ partners }: { partners: PartnerRedemptionView[] }) => {
  const { t, i18n } = useTranslation()

  return (
    <div className="mt-3 rounded-md bg-white px-3 py-2.5 ring-1 ring-gray-200">
      <p className="text-xs font-medium text-gray-900">{t('benefits.partnersHeading')}</p>
      <ul className="mt-1.5 divide-y divide-gray-100">
        {partners.map((partner) => {
          const worth = partner.worth ?? partner.benefitLabel
          const url = partner.redemption.url
          return (
            <li key={partner.partnerUrn} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{partner.partnerName}</p>
                {worth && <p className="text-xs text-gray-600">{worth}</p>}
              </div>
              {!!url && (
                <a
                  href={url}
                  // Off DUPR entirely, so it opens away from this page and cannot reach back into it
                  // through `window.opener`.
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded-full bg-[#3A79E0] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2D5FB8]"
                >
                  {redemptionText(i18n, partner.redemption.linkLabel) ||
                    t('benefits.useAtPartner', { partnerName: partner.partnerName })}
                </a>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
